import React, { useEffect, useRef } from 'react';
import { Linkedin } from 'lucide-react';
import Container from '../ui/Container';
import { TeamMember } from '../../types';

const TeamMemberCard = ({ member }: { member: TeamMember }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  return (
    <div 
      ref={cardRef} 
      className="bg-white rounded-lg shadow-lg overflow-hidden opacity-0 transition-all duration-300"
    >
      <div className="relative">
        <img 
          src={member.image} 
          alt={member.name} 
          className="w-full h-64 object-cover"
        />
        <div className="absolute top-4 right-4">
          <a 
            href="#" 
            className="bg-white p-2 rounded-full shadow-md text-blue-900 hover:text-orange-500 transition-colors"
          >
            <Linkedin size={20} />
          </a>
        </div>
      </div>
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900">{member.name}</h3>
        <p className="text-orange-600 mb-3">{member.position}</p>
        <p className="text-gray-600">{member.bio}</p>
      </div>
    </div>
  );
};

const Team = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const teamMembers: TeamMember[] = [
    {
      name: 'Priya Sharma',
      position: 'Founder & CEO',
      bio: 'With 10+ years in tech innovation, Priya leads HandsForNeeds with a vision to transform how services are delivered through technology.',
      image: 'https://images.pexels.com/photos/3796217/pexels-photo-3796217.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    },
    {
      name: 'Rahul Patel',
      position: 'CTO',
      bio: 'Rahul brings extensive expertise in platform development and scalable architecture to build robust and user-friendly solutions.',
      image: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    },
    {
      name: 'Aisha Khan',
      position: 'Head of Operations',
      bio: 'Aisha ensures smooth operations across all platforms, focusing on service quality and customer satisfaction.',
      image: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    }
  ];

  return (
    <section id="team" className="py-16 md:py-24 bg-gray-50">
      <Container>
        <div ref={sectionRef} className="text-center mb-12 opacity-0 transition-opacity duration-1000">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Leadership Team</h2>
          <div className="w-20 h-1 bg-orange-500 mx-auto mb-6"></div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Meet the dedicated team behind HandsForNeeds who are passionate about creating innovative solutions that connect people.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {teamMembers.map((member, index) => (
            <TeamMemberCard key={index} member={member} />
          ))}
        </div>
      </Container>
    </section>
  );
};

export default Team;